<script setup lang="ts">
/**
 * Настройки проекта — макеты 24:2, 84:233, 84:404, 84:575.
 *
 * Вкладка живёт в адресе (`?tab=members`): настройки пересылают в чате
 * («поставь Тимуру наблюдателя»), и ссылка должна открывать нужную.
 *
 * Кнопки «Отменить» и «Сохранить» общие на все вкладки — как в макете. Правки
 * копятся в черновике: у настроек доски нет изменения, которое имело бы смысл
 * в одиночку, а переключение вкладки не должно терять набранное.
 *
 * Вкладки «Импорт» из макета здесь нет: импорта в трекере нет ни ручкой, ни
 * задачей эпика (docs/design-gaps.md).
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { AlertTriangle } from 'lucide-vue-next'
import { computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage, errorRoute } from '@/api/errors'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import ColumnsTab from '@/features/settings/components/ColumnsTab.vue'
import GeneralTab from '@/features/settings/components/GeneralTab.vue'
import MembersTab from '@/features/settings/components/MembersTab.vue'
import TypesTab from '@/features/settings/components/TypesTab.vue'
import { useSettingsStore } from '@/features/settings/store'

const TABS = ['columns', 'members', 'types', 'general'] as const

type Tab = (typeof TABS)[number]

const TAB_VIEWS = {
  columns: ColumnsTab,
  members: MembersTab,
  types: TypesTab,
  general: GeneralTab,
}

const { t } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()
const store = useSettingsStore()

const projectKey = computed(() => String(route.params.key ?? ''))

const tab = computed<Tab>(() => {
  const asked = String(route.query.tab ?? '')

  return (TABS as readonly string[]).includes(asked) ? (asked as Tab) : 'columns'
})

const breadcrumbs = computed(() => [
  { label: t('nav.projects'), to: '/projects' },
  { label: store.project?.name || projectKey.value, to: `/p/${projectKey.value}/board` },
  { label: t('views.settings') },
])

const openSettings = async () => {
  await store.open(projectKey.value)

  const failureRoute = errorRoute(store.error, projectKey.value)

  if (failureRoute) {
    await router.replace(failureRoute)

    return
  }

  // Настройки правит только администратор проекта — так же считает и сервис.
  // Показать экран, на котором каждая кнопка кончается отказом, было бы хуже.
  if (store.project && !store.isAdmin) await router.replace({ name: 'forbidden' })
}

const save = async () => {
  try {
    await store.save()
    toast.success(t('settings.saved'))
  } catch (failure) {
    toast.error(errorMessage(failure, t('settings.saveFailed')))
  }
}

const onFailed = (failure: unknown) => {
  toast.error(errorMessage(failure, t('settings.saveFailed')))
}

onMounted(openSettings)

watch(projectKey, openSettings)
</script>

<template>
  <TrackerToolbar :breadcrumbs="breadcrumbs">
    <template v-if="store.project">
      <!-- «Отменить», а не «Отмена» из словаря пакета: здесь это не закрытие
           окна, а возврат экрана к тому, что на сервере (макет 34:468). -->
      <AppButton variant="secondary" size="small" :disabled="!store.isDirty" @click="store.reset">
        {{ t('settings.cancel') }}
      </AppButton>
      <AppButton
        variant="primary"
        size="small"
        :disabled="!store.isDirty"
        :loading="store.isSaving"
        @click="save"
      >
        {{ t('settings.save') }}
      </AppButton>
    </template>
  </TrackerToolbar>

  <div class="settings">
    <nav class="settings__tabs" :aria-label="t('views.settings')">
      <RouterLink
        v-for="name in TABS"
        :key="name"
        class="settings__tab"
        :class="{ 'settings__tab--active': name === tab }"
        :to="{ query: { tab: name } }"
      >
        {{ t(`settings.tabs.${name}`) }}
      </RouterLink>
    </nav>

    <div v-if="store.isLoading && !store.project" class="settings__skeleton" />

    <TrackerEmptyState
      v-else-if="store.error"
      :icon="AlertTriangle"
      :title="t('settings.error.title')"
      :description="t('settings.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="openSettings">{{ t('board.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <component :is="TAB_VIEWS[tab]" v-else-if="store.project" @failed="onFailed" />
  </div>
</template>

<style scoped>
.settings {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 28px 48px;
}

.settings__tabs {
  display: flex;
  gap: 20px;
  border-bottom: 1px solid var(--color-border);
}

.settings__tab {
  padding: 2px 0 10px;
  border-bottom: 2px solid transparent;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.settings__tab:hover {
  color: var(--color-text-secondary);
}

.settings__tab--active {
  border-bottom-color: var(--color-primary);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.settings__skeleton {
  height: 320px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}
</style>
